import { useEffect, useRef, useState } from "react";
import Select, { StylesConfig } from "react-select";
import { useFormContext } from "react-hook-form";
import styled from "@emotion/styled";


export interface SegSelectOption {
  value: string;
  label: string;
}

export interface SegSelectGrupedOption {
  label: string;
  options: any[]
}

interface SegSelectProps {
  options: SegSelectOption[];
  isGrupable?:boolean;
  width?: string;
  placeholder?: string;
  isMulti?: boolean;
  borderColor?: string;
  hoverBorderColor?: string;
  focusBorderColor?: string;
  selected?: SegSelectOption | null;
  setSelected?: (selected: SegSelectOption) => void;
  className?: string;
  id?: string;
  name: string;
  disabled?: boolean;
  loading?: boolean;
  openDefault?: boolean;
  handleSubmit?: (value: SegSelectOption) => void
  /** Se dispara cuando el control pierde el foco (para validar "al salir"). */
  onBlur?: () => void;
  autoFoco?: boolean;
  label?: string;
  labelColor?: string;
  isClearable?: boolean;
  height?: "sm"|"md"|"lg";
  /**
   * Fuerza (o desactiva) el input de búsqueda. Si no se pasa, se decide solo:
   * en desktop siempre hay búsqueda, y en táctil solo cuando la lista es larga
   * (ver UMBRAL_BUSQUEDA_TACTIL).
   */
  buscable?: boolean;
  /** Id del texto visible que nombra al campo (para lectores de pantalla). Opcional. */
  ariaLabelledBy?: string;
  /**
   * En táctil, al abrir con el teclado sube la vista solo lo necesario para que la
   * lista quede sobre el teclado (nada si ya entra), en vez de llevar el campo hasta
   * arriba de todo. Para pantallas cortas donde el salto esconde el resto. Opcional.
   */
  subirSoloLoJusto?: boolean;
  empresaColor?: {
    selected: string;
    hover: string;
  } | null;
}

/**
 * A partir de cuántas opciones vale la pena ofrecer búsqueda en un dispositivo
 * táctil. Por debajo de este número se elige más rápido con el dedo que
 * tipeando, y el teclado en pantalla solo estorba. Por encima (provincia,
 * localidad, marca, modelo) scrollear la lista es el problema.
 */
const UMBRAL_BUSQUEDA_TACTIL = 8;

/** Aire entre el borde superior del área visible y el campo, al acomodar la vista. */
const MARGEN_SUPERIOR = 12;

const Label = styled.label<{ labelColor: string }>`
color: ${({ labelColor }) => labelColor} !important;
`;


export const SegSelect = ({
  options = [],
  isGrupable = false,
  width = "18em",
  label,
  labelColor,
  placeholder = "Seleccioná",
  isMulti = false,
  borderColor = "black",
  hoverBorderColor = "black",
  focusBorderColor = "#2C7DFD",
  selected = null,
  setSelected,
  className,
  name,
  id,
  disabled = false,
  loading = false,
  openDefault = true,
  handleSubmit,
  onBlur,
  autoFoco = false,
  isClearable = false,
  height = "lg",
  buscable,
  ariaLabelledBy,
  subirSoloLoJusto = false,
  empresaColor = null
}: SegSelectProps) => {
  const { setValue } = useFormContext(); // Obtén la instancia del formulario usando useFormContext
  const esTactil =
    typeof window !== "undefined" &&
    !!window.matchMedia &&
    window.matchMedia("(pointer: coarse)").matches;

  // En táctil el input de búsqueda abre el teclado en pantalla, así que solo se
  // ofrece cuando la lista es lo bastante larga como para que scrollearla sea
  // peor que tipear. Que el teclado no tape el campo lo resuelve acomodarVista().
  const esBuscable =
    buscable !== undefined
      ? buscable
      : !esTactil || options.length >= UMBRAL_BUSQUEDA_TACTIL;

  const [isOpen, setIsOpen] = useState<boolean>()
  const optionsRef = useRef<any>(null);
  /** Baja del listener de visualViewport, mientras el menú esté abierto. */
  const limpiarTeclado = useRef<(() => void) | null>(null);

  /**
   * Sube la vista para que el campo (y por lo tanto el menú de opciones que se
   * despliega debajo) queden sobre el teclado.
   *
   * El `<meta viewport>` del sitio no declara `interactive-widget`, así que al
   * abrirse el teclado el *layout* viewport no cambia de alto: solo se encoge el
   * *visual* viewport. Por eso `scrollIntoView` no sirve — cree que hay lugar de
   * sobra y deja el campo abajo, tapado. Hay que medir con `window.visualViewport`
   * y scrollear a mano.
   *
   * Se mide el control de react-select (`controlRef`) en vez de envolver todo en
   * un div propio: SegSelect se usa en muchos layouts del sitio y agregar un nodo
   * al DOM podía correr los que son flex/grid.
   */
  const acomodarVista = () => {
    const control: HTMLDivElement | null | undefined =
      optionsRef.current?.controlRef;
    if (!control) return;

    const vv = window.visualViewport;

    const reposicionar = () => {
      const caja = control.getBoundingClientRect();
      // Tope del área realmente visible, en coordenadas del layout viewport.
      const topeVisible = vv ? vv.offsetTop : 0;
      let desplazamiento = caja.top - topeVisible - MARGEN_SUPERIOR;

      if (subirSoloLoJusto && vv) {
        // Lo que la lista (o el campo, si todavía no se dibujó) se mete debajo del
        // teclado; nunca más que llevar el campo arriba de todo.
        const lista: HTMLElement | null | undefined = optionsRef.current?.menuListRef;
        const fondo = lista ? lista.getBoundingClientRect().bottom : caja.bottom;
        const falta = fondo - (vv.offsetTop + vv.height) + MARGEN_SUPERIOR;
        desplazamiento = Math.min(desplazamiento, Math.max(0, falta));
      }

      if (Math.abs(desplazamiento) > 1) {
        // Instantáneo y no "smooth": el scroll suave compite con el que hace el
        // navegador por su cuenta al enfocar el input, y los dos quedan a mitad
        // de camino (el campo termina pegado al borde del teclado).
        window.scrollBy({ top: desplazamiento, behavior: "auto" });
      }
    };

    // El teclado no aparece de golpe: visualViewport dispara VARIOS `resize`
    // mientras se desliza. Hay que reposicionar en cada uno para que gane la
    // última medición (teclado ya completo); quedarse con el primero mide un
    // teclado a medio camino y sube de menos.
    const alRedimensionar = () => requestAnimationFrame(reposicionar);
    vv?.addEventListener("resize", alRedimensionar);
    limpiarTeclado.current = () =>
      vv?.removeEventListener("resize", alRedimensionar);

    // Respaldo: si el teclado ya estaba abierto por otro campo del formulario no
    // hay ningún `resize` que escuchar, y tampoco lo hay sin visualViewport.
    setTimeout(reposicionar, 350);
  };

  /** Corta el seguimiento del teclado al cerrar el menú o desmontar. */
  const dejarDeSeguirTeclado = () => {
    limpiarTeclado.current?.();
    limpiarTeclado.current = null;
  };

  // Un menú abierto al desmontar dejaría el listener colgado.
  useEffect(() => () => { limpiarTeclado.current?.(); }, []);

  const handleChange = (option: any) => {
    setValue(name, option ? option.value : null); // Usa setValue para actualizar el valor del campo
    setSelected && setSelected(option);
    setIsOpen(false)
    dejarDeSeguirTeclado();
    if (handleSubmit) {
      handleSubmit(option)
    }
  };
  const toggleMenu = () => {
    setIsOpen(current => !current)
    if (esTactil && esBuscable) {
      acomodarVista();
    }
  };

  useEffect(() => {

    setIsOpen(openDefault)
  }, [openDefault])

  useEffect(() => {
    if (selected != null && isClearable && optionsRef.current) {
      optionsRef.current.clearValue();
      if (options.length === 1) {
        setValue(name, options[0] ? options[0].value : null);
        setSelected && setSelected(options[0]);
      }
    }
  }, [options])

  // useEffect(() => {
  //   const def:SegSelectOption = {value: '' , label: ''}
  //   if (selected && selected !== def ) {
  //     setIsOpen(false)
  //   }
  // }, [selected])

  //const style: StylesConfig = {
  const style: StylesConfig<SegSelectOption, boolean, any>  = {
    menuList: (base: any) => ({
      ...base,
      maxHeight: height === "sm" ? "120px" : height === "md" ? "170px" : "300px"
    }),
    menu: (base: any) => ({
      ...base,
      scrollbarColor: "white",
      // Por encima de barras sticky (p.ej. el footer del cotizador, z-index 2)
      // para que las opciones no queden tapadas.
      zIndex: 20,
    }),
    control: (base: any) => ({
      ...base,
      boxShadow: "none",
      borderColor: borderColor,
      width: width,
      "&:hover": {
        borderColor: hoverBorderColor,
      },
      "&:focus-within": {
        borderColor: focusBorderColor,
      },
      "&::-webkit-scrollbar": {
        width: 12,
        borderRadius: 20,
      },
    }),
    option: (base: any, state) => ({
      ...base,
      paddingLeft: isGrupable && state.data.value !== "separator" && "24px",
      color: state.data.value === "separator" && "#000",
      fontWeight: state.data.value === "separator" && 600,
      background: empresaColor && state.data.value === selected?.value && empresaColor.selected,
      backgroundColor: state.data.value === "OtraActividad" && "#ffd332"
      /*"&:hover": {
        background: empresaColor && empresaColor.hover,
      },*/
      
    }),
    input: (base: any) => ({
      gridArea: "1/1/2/3",
    }),
  };


  const isOptionDisabled = (option: SegSelectOption) => {
    return option.value === 'separator';
  };

 
  return (
    <>
      {label !== "" ? (
        <Label className="form-label" labelColor={labelColor!}>
          {label}
        </Label>
      ) : (
        ""
      )}
      <Select
        ref={optionsRef}
        className={className}
        isMulti={isMulti}
        styles={style}
        options={options ? options : []}
        value={selected}
        onChange={handleChange}
        onBlur={onBlur}
        isSearchable={esBuscable}
        // Al elegir, saca el foco del input para que el teclado se cierre solo.
        blurInputOnSelect={esTactil}
        // El scroll lo maneja acomodarVista(), que sí tiene en cuenta el teclado.
        // El de react-select mide el layout viewport y pelearía con el nuestro.
        menuShouldScrollIntoView={!esTactil}
        placeholder={placeholder}
        id={id}
        aria-labelledby={ariaLabelledBy}
        name={name}
        isClearable={isClearable}
        isDisabled={disabled}
        isLoading={loading}
        menuIsOpen={isOpen}
        onMenuOpen={toggleMenu}
        onMenuClose={() => { setIsOpen(false); dejarDeSeguirTeclado(); }}
        isOptionDisabled={(option) => isOptionDisabled(option as SegSelectOption)}
        autoFocus={autoFoco}
        // "auto" mide el layout viewport, que no se entera del teclado: cree que
        // sobra espacio abajo y despliega el menú justo detrás de él. Como en
        // táctil acomodarVista() deja el campo arriba, abajo es siempre correcto.
        menuPlacement={esTactil && esBuscable ? "bottom" : "auto"}
        noOptionsMessage={() => "Sin resultados"}
      />
    </>
  );
};
